import { Injectable } from '@angular/core';
import { Auth } from 'aws-amplify';
import { configuracaoDoCognitoAuth, tipoAutenticacaoMfaCognito } from 'src/environments/environment';
import { CognitoMfaAuthenticationException } from '../../exceptions/cognito-mfa-authentication-exception';
import { CognitoResetPasswordRequiredException } from '../../exceptions/cognito-reset-password-required-exception';
import { CognitoAmplifyConfig } from '../../types/cognito-amplify-config';

@Injectable({ providedIn: 'root' })
export class CognitoService {
  private sessaoUsuarioCognito: any;

  constructor() {
    Auth.configure(this.construirConfiguracoesDoAmplify());
  }

  /**
   * Realiza o login na plataforma Cognito utilizando as credenciais fornecidas pelo usuário.
   * Este método pode não armazenar a sessão completa caso alguma validação adicional de login seja necessária,
   * como, por exemplo, se o usuário precisar enviar um token de autenticação multifator (MFA),
   * a sessão armazenada não estará completa. Caso contrário, armazena a sessão Cognito do usuário logado no serviço.
   * @param usuario Usuario do cognito
   * @param senha Senha do cognito
   */
  public async autenticar(usuario: string, senha: string): Promise<any> {
    try {
      this.sessaoUsuarioCognito = await Auth.signIn(usuario, senha);
      this.salvarSessaoDoUsuarioCognitoNoArmazenamentoLocal();
    } catch (error) {
      if (error.message.includes('Password reset')) throw new CognitoResetPasswordRequiredException(error.message);
      throw new CognitoMfaAuthenticationException('Nome ou senha de usuário inválidos.');
    }
  }

  /**
   * Verifica se o usuário também precisa fornecer uma nova senha, pois está usando uma senha provisória.
   * Este método só pode ser usado com uma sessão armazenada.
   */
  public temDesafioDeSenhaProvisoria(): boolean {
    return this.sessaoUsuarioCognito.challengeName === 'NEW_PASSWORD_REQUIRED';
  }

  /**
   * Envia um email com a senha provisória para o usuário utilizando a plataforma Cognito.
   * @param usuario - Usuario do cognito
   */
  public async enviarCodigoParaResetDeSenha(usuario: string): Promise<any> {
    return await Auth.forgotPassword(usuario);
  }

  public async realizarResetDeSenha(usuario: string, codigo: string, novaSenha: string): Promise<any> {
    return await Auth.forgotPasswordSubmit(usuario, codigo, novaSenha);
  }

  /**
   * Metodo que realiza a alteracao da senha do usuario autenticado na plataforma Cognito.
   * @param novaSenha
   */
  public async completarEnvioDeNovaSenha(novaSenha: string): Promise<void> {
    this.sessaoUsuarioCognito = await Auth.completeNewPassword(this.sessaoUsuarioCognito, novaSenha);
    this.salvarSessaoDoUsuarioCognitoNoArmazenamentoLocal();
  }

  /**
   * Verifica se o usuário também precisa fornecer um token de autenticação multifator (MFA).
   */
  public temDesafioDeTokenMfa(): boolean {
    if (this.estaUsandoFluxoDeAutenticacaoCustomizado()) return true;
    return !(this.sessaoUsuarioCognito.preferredMFA && this.sessaoUsuarioCognito.preferredMFA === 'NOMFA');
  }

  /**
   * Envia o token de autenticação multifator utilizando a sessão do usuário no Cognito como parâmetro.
   * Para utilizar este método, o usuário deve estar pré-logado.
   * @param token token de autenticação multifator fornecido via app, sms ou email.
   * Depende da configuração do Cognito.
   */
  public async enviarTokenMfa(token: string): Promise<void> {
    try {
      if (this.estaUsandoFluxoDeAutenticacaoCustomizado()) {
        this.sessaoUsuarioCognito = await Auth.sendCustomChallengeAnswer(this.sessaoUsuarioCognito, token);
        this.salvarSessaoDoUsuarioCognitoNoArmazenamentoLocal();
        return;
      }

      this.sessaoUsuarioCognito = await Auth.confirmSignIn(
        this.sessaoUsuarioCognito,
        token,
        this.obterTipoDeAutenticacaoMfaCognito()
      );

      this.salvarSessaoDoUsuarioCognitoNoArmazenamentoLocal();
    } catch (error) {
      throw new CognitoMfaAuthenticationException(error.message);
    }
  }

  /**
   * Retorna o refresh token da sessão do usuário atual autenticado no Cognito.
   */
  public async obterRefreshToken(): Promise<any> {
    const sessaoAtual = await Auth.currentSession();
    return sessaoAtual.getRefreshToken();
  }

  /**
   * Verifica se o refresh token da sessão do usuário atual autenticado no Cognito é válido.
   */
  public async refreshTokenValido(): Promise<boolean> {
    try {
      await this.obterRefreshToken();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Retorna o json web token da sessão do usuário atual autenticado no Cognito.
   */
  public async obterJsonWebToken(): Promise<string> {
    const sessaoAtual = await Auth.currentSession();
    return sessaoAtual.getAccessToken().getJwtToken();
  }

  /**
   * Retorna o token Id da sessão do usuário atual autenticado no Cognito.
   */
  public async obterTokenId(): Promise<string> {
    const sessaoAtual = await Auth.currentSession();
    return sessaoAtual.getAccessToken().getJwtToken();
  }

  /**
   * Retorna o access token da sessão do usuário atual autenticado no Cognito.
   */
  public async obterAccessToken(): Promise<string> {
    const sessaoAtual = await Auth.currentSession();
    return sessaoAtual.getAccessToken().getJwtToken();
  }

  /**
   * Retorna o id do cognito do usuário atual autenticado no Cognito.
   */
  public async obterIdCognito(): Promise<string> {
    const sessaoAtual = await Auth.currentSession();
    return sessaoAtual.getAccessToken().payload.sub;
  }

  /**
   * Atualiza a sessão atual do usuário no Cognito utilizando o refresh token armazenado no armazenamento local.
   * É necessário que o usuário esteja logado para esse metodo funcionar.
   */
  public async atualizarSessaoCognito(): Promise<void> {
    const usuarioAtual = await Auth.currentAuthenticatedUser();
    if (!usuarioAtual)
      throw new Error('Não foi possivel atualizar a sessão do usuário do cognito. Nenhum usuário logado.');

    const refreshToken = await this.obterRefreshToken();
    if (!refreshToken)
      throw new Error('Não foi possivel atualizar a sessão do usuário do cognito. Refresh token não encontrado.');

    await usuarioAtual.refreshSession(refreshToken, (error: any, sessaoAtualizada: any) => {
      if (error) {
        console.log(error.message);
        throw new Error('Não foi possivel atualizar a sessão do usuário do cognito');
      }
      this.sessaoUsuarioCognito = sessaoAtualizada;
      this.salvarSessaoDoUsuarioCognitoNoArmazenamentoLocal();
    });
  }

  /**
   * Realiza o logout na plataforma Cognito e remova a sessão armazenada do serviço e do armazenamento local.
   */
  public async deslogar(): Promise<void> {
    await Auth.signOut();
    this.sessaoUsuarioCognito = null;
    localStorage.removeItem('sessaoUsuarioCognito');
  }

  /**
   * Metodo que realiza a alteracao da senha do usuario autenticado na plataforma Cognito.
   * Este metodo so pode ser utilizado com uma sessao armazenada.
   * @param senhaAntiga A senha antiga do usuario
   * @param novaSenha A nova senha do usuario
   */
  public async alterarSenha(senhaAntiga: string, novaSenha: string): Promise<boolean> {
    const response = await Auth.changePassword(this.sessaoUsuarioCognito, senhaAntiga, novaSenha);
    return response === 'SUCCESS';
  }

  /**
   * Método para obter a sessão do usuário do Cognito armazenada no serviço.
   * Se não houver sessão armazenada, ele tentará carregar a sessão do armazenamento local.
   * Se não houver sessão no armazenamento local, ele lançará um erro.
   */
  public obterSessaoDeUsuarioCognito(): any {
    if (this.sessaoUsuarioCognito) return this.sessaoUsuarioCognito;
    this.lerDadosDaSessaoDoUsuarioCognitoDoArmazenametoLocal();
    if (!this.sessaoUsuarioCognito) {
      throw new Error('Nehuma sessão de usuário encontrada. Realize sua autenticação primeiro');
    }
    return this.sessaoUsuarioCognito;
  }

  /**
   * Verifica e retorna se o usuário é membro de um grupo de usuários específico
   * dentro do grupo de usuários cognito.
   * Caso nenhum grupo seja encontrado, retorna negativo.
   * @param grupoSelecionado Nome do grupo de usuários
   */
  public usuarioMembroDeUmGrupoEspecifico(grupoSelecionado: string): boolean {
    const grupos = this.obterGruposDeUsuarioCognito();
    if (!grupos) return false;
    const membroDoGrupo = grupos.find((grupo) => grupo == grupoSelecionado);
    return !!membroDoGrupo;
  }

  /**
   * Retorna se existe uma sessão de usuário cognito autenticada.
   */
  public usuarioLogado(): boolean {
    this.lerDadosDaSessaoDoUsuarioCognitoDoArmazenametoLocal();
    return !!this.sessaoUsuarioCognito;
  }

  /**
   * Retorna o cabeçalho de autenticação para requisições autenticadas.
   */
  public async montarCabecalhoAutenticado(type: "json"|"pdf" = "json"): Promise<Object> {
    const jsonWebToken: string = await this.obterJsonWebToken();

    if (type == "json") {
      return { 'Content-Type': 'application/json', Authorization: `Bearer ${jsonWebToken}`};
    }

    if (type == "pdf") {
      return { accept: "application/pdf", Authorization: `Bearer ${jsonWebToken}`}
    } 
  }

  // ======== Métodos privados ========
  /**
   * Método que salva a sessão do usuário cognito no armazenamento local caso ela exista.
   * @private
   */
  private salvarSessaoDoUsuarioCognitoNoArmazenamentoLocal() {
    if (this.sessaoUsuarioCognito) {
      localStorage.setItem('sessaoUsuarioCognito', JSON.stringify(this.sessaoUsuarioCognito));
    }
  }

  /**
   * Método que realiza a leitura da sessão do usuário cognito do armazenamento local.
   * @private
   */
  private lerDadosDaSessaoDoUsuarioCognitoDoArmazenametoLocal() {
    const sessaoUsuario = localStorage.getItem('sessaoUsuarioCognito');
    this.sessaoUsuarioCognito = JSON.parse(sessaoUsuario!);
  }

  /**
   * Metodo que realiza a construção do objeto de configuração do Amplify.
   * @private
   */
  private construirConfiguracoesDoAmplify() {
    const configurations: CognitoAmplifyConfig = { ...configuracaoDoCognitoAuth };
    if (this.estaUsandoFluxoDeAutenticacaoCustomizado()) configurations.authenticationFlowType = 'CUSTOM_AUTH';
    return configurations;
  }

  /**
   * Valida se a aplicação está usando algum fluxo de autenticação customizado.
   * @private
   */
  private estaUsandoFluxoDeAutenticacaoCustomizado(): boolean {
    return tipoAutenticacaoMfaCognito === 'email';
  }

  /**
   * Obtem o tipo de autenticação multifator (MFA) que está sendo utilizado.
   * @private
   */
  private obterTipoDeAutenticacaoMfaCognito(): 'SOFTWARE_TOKEN_MFA' | 'SMS_MFA' {
    return tipoAutenticacaoMfaCognito === 'app' ? 'SOFTWARE_TOKEN_MFA' : 'SMS_MFA';
  }

  /**
   * Retorna um array com os grupos de usuário do Cognito.
   * @private
   */
  private obterGruposDeUsuarioCognito(): Array<string> {
    if (!this.sessaoUsuarioCognito) return [];
    return this.sessaoUsuarioCognito.signInUserSession.idToken.payload['cognito:groups'];
  }
}
